// Central game state management
// Holds all gladiators, teams, and game progress

import { GladiatorData, createGladiator, resetForNewMonth, resetForNewYear, shouldRetire } from '../entities/Gladiator';
import { TeamData, createTeam, setTeamStartValues, resetTeamForNewMonth, resetTeamForNewYear, modifyMoney, getTotalSalary, removeGladiator, addGladiator } from '../entities/Team';
import { Race, getRandomRace } from '../data/races';
import { GAME_CONSTANTS, MONTH_SCHEDULE, MONTH_TYPE } from '../data/constants';
import { AI_TEAM_NAMES } from '../data/names';
import { getSellPrice, ALL_ITEMS } from '../data/items';
import { randomRange } from '../utils/dice';

export interface Division {
  index: number;
  startMoney: number;
  teamIds: string[];
}

export interface GameState {
  // Time
  month: number;
  year: number;

  // Entities
  gladiators: Map<string, GladiatorData>;
  teams: Map<string, TeamData>;

  // Divisions (4 divisions, 4 teams each)
  divisions: Division[];

  // Current session state
  currentTeamId: string | null;
  currentGladiatorId: string | null;
  raceOfMonth: Race;

  // Recruitment
  recruitableGladiatorIds: string[];
  recruitOrder: string[]; // Team IDs in recruit order
  currentRecruitTeamIdx: number;
  currentRecruitRound: number;
  teamsCompletedRecruitRound: Set<string>; // Teams that have acted this round

  // Shop
  discountedItemIds: Set<string>; // Item IDs that are discounted this month

  // Player info
  playerTeamIds: string[];
}

// Create initial game state
export function createGameState(): GameState {
  return {
    month: 0,
    year: GAME_CONSTANTS.START_YEAR,
    gladiators: new Map(),
    teams: new Map(),
    divisions: [],
    currentTeamId: null,
    currentGladiatorId: null,
    raceOfMonth: getRandomRace(),
    recruitableGladiatorIds: [],
    recruitOrder: [],
    currentRecruitTeamIdx: 0,
    currentRecruitRound: 1,
    teamsCompletedRecruitRound: new Set(),
    discountedItemIds: new Set(),
    playerTeamIds: [],
  };
}

// Initialize divisions
export function initDivisions(state: GameState): void {
  state.divisions = [
    { index: 0, startMoney: GAME_CONSTANTS.START_MONEY_DIV_1, teamIds: [] },
    { index: 1, startMoney: GAME_CONSTANTS.START_MONEY_DIV_2, teamIds: [] },
    { index: 2, startMoney: GAME_CONSTANTS.START_MONEY_DIV_3, teamIds: [] },
    { index: 3, startMoney: GAME_CONSTANTS.START_MONEY_DIV_4, teamIds: [] },
  ];
}

// Create AI teams
export function createAITeams(state: GameState): void {
  // Division 1
  for (const name of AI_TEAM_NAMES.DIV_1) {
    const team = createTeam(name, null);
    team.division = 0;
    state.teams.set(team.id, team);
    state.divisions[0].teamIds.push(team.id);
  }

  // Division 2
  for (const name of AI_TEAM_NAMES.DIV_2) {
    const team = createTeam(name, null);
    team.division = 1;
    state.teams.set(team.id, team);
    state.divisions[1].teamIds.push(team.id);
  }

  // Division 3
  for (const name of AI_TEAM_NAMES.DIV_3) {
    const team = createTeam(name, null);
    team.division = 2;
    state.teams.set(team.id, team);
    state.divisions[2].teamIds.push(team.id);
  }

  // Division 4 - leave room for player teams
  for (const name of AI_TEAM_NAMES.DIV_4) {
    const team = createTeam(name, null);
    team.division = 3;
    state.teams.set(team.id, team);
    state.divisions[3].teamIds.push(team.id);
  }
}

// Create player teams
export function createPlayerTeams(state: GameState, playerTeamNames: string[]): void {
  for (let i = 0; i < playerTeamNames.length && i < 4; i++) {
    const playerId = `player-${i}`;
    const team = createTeam(playerTeamNames[i], playerId);
    team.division = 3;
    state.teams.set(team.id, team);
    state.playerTeamIds.push(team.id);

    // Add player team to division 4
    // If AI team exists in that slot, move to fill gap
    if (state.divisions[3].teamIds.length > 3 - i) {
      // Remove extra AI team
      state.divisions[3].teamIds.pop();
    }
    state.divisions[3].teamIds.push(team.id);
  }
}

// Add starting gladiators to a team
export function addStartingGladiators(state: GameState, team: TeamData): void {
  const count = randomRange(1, GAME_CONSTANTS.MAX_START_GLADIATORS);
  const teamRaceIds = new Set<number>();

  for (let i = 0; i < count; i++) {
    // Get a random race not already on team
    let race = getRandomRace();
    let attempts = 0;
    while (teamRaceIds.has(race.id) && attempts < 36) {
      race = getRandomRace();
      attempts++;
    }

    if (teamRaceIds.has(race.id)) continue;

    const gladiator = createGladiator(race);
    gladiator.ownerId = team.id;
    state.gladiators.set(gladiator.id, gladiator);
    addGladiator(team, gladiator.id);
    teamRaceIds.add(race.id);
  }
}

// Set starting values for all teams
export function setAllTeamStartValues(state: GameState): void {
  for (const division of state.divisions) {
    for (const teamId of division.teamIds) {
      const team = state.teams.get(teamId);
      if (team) {
        setTeamStartValues(team, division.startMoney);
      }
    }
  }
}

// Setup recruit order
export function setupRecruitOrder(state: GameState): void {
  state.recruitOrder = [];
  for (const division of state.divisions) {
    for (const teamId of division.teamIds) {
      state.recruitOrder.push(teamId);
    }
  }
}

// Create recruitable gladiators
export function createRecruitableGladiators(state: GameState): void {
  state.recruitableGladiatorIds = [];
  for (let i = 0; i < GAME_CONSTANTS.RECRUIT_GLADIATOR_COUNT; i++) {
    const race = getRandomRace();
    const gladiator = createGladiator(race);
    state.gladiators.set(gladiator.id, gladiator);
    state.recruitableGladiatorIds.push(gladiator.id);
  }
}

// Generate discounted items for the month (one from each category)
export function generateDiscountedItems(state: GameState): void {
  state.discountedItemIds = new Set();

  // Pick one random item from each category to discount
  const categories = [
    ALL_ITEMS.filter(i => i.subtype === 0), // Spears
    ALL_ITEMS.filter(i => i.subtype === 1), // Swords
    ALL_ITEMS.filter(i => i.subtype === 2), // Axes
    ALL_ITEMS.filter(i => i.subtype === 3), // Maces
    ALL_ITEMS.filter(i => i.subtype === 4), // Bows
    ALL_ITEMS.filter(i => i.subtype === 5), // Crossbows
    ALL_ITEMS.filter(i => i.type === 2),    // Armors
  ];

  for (const category of categories) {
    if (category.length > 0) {
      const randomItem = category[Math.floor(Math.random() * category.length)];
      state.discountedItemIds.add(randomItem.id);
    }
  }
}

// Check if an item is discounted this month
export function isItemDiscounted(state: GameState, itemId: string): boolean {
  return state.discountedItemIds.has(itemId);
}

// Reset recruitment state for a new round
export function resetRecruitmentForNewMonth(state: GameState): void {
  state.currentRecruitTeamIdx = 0;
  state.currentRecruitRound = 1;
  state.teamsCompletedRecruitRound = new Set();
}

// Start a new game
export function startNewGame(state: GameState, playerTeamNames: string[], autoStartTeams: boolean = true): void {
  initDivisions(state);
  createAITeams(state);
  createPlayerTeams(state, playerTeamNames);

  // Add starting gladiators
  for (const [, team] of state.teams) {
    if (team.playerId === null || autoStartTeams) {
      addStartingGladiators(state, team);
    }
  }

  setAllTeamStartValues(state);
  setupRecruitOrder(state);
  createRecruitableGladiators(state);
  generateDiscountedItems(state);

  // Set current team to first player team
  if (state.playerTeamIds.length > 0) {
    state.currentTeamId = state.playerTeamIds[0];
  }

  // Set race of month
  state.raceOfMonth = getRandomRace();
}

// Get current month type
export function getCurrentMonthType(state: GameState): number {
  return MONTH_SCHEDULE[state.month];
}

// Check if it's a battle month
export function isBattleMonth(state: GameState): boolean {
  const type = getCurrentMonthType(state);
  return type === MONTH_TYPE.DIVISION_BATTLE || type === MONTH_TYPE.CUP_BATTLE;
}

// Progress to next month
export function progressToNextMonth(state: GameState): void {
  // Pay salaries
  for (const [, team] of state.teams) {
    const salary = getTotalSalary(team, state.gladiators);
    modifyMoney(team, -salary);
    resetTeamForNewMonth(team);
  }

  // Reset gladiators
  for (const [, gladiator] of state.gladiators) {
    resetForNewMonth(gladiator);
  }

  // Progress time
  if (state.month < 11) {
    state.month++;
  } else {
    state.month = 0;
    state.year++;
    progressToNewYear(state);
  }

  // Reset recruitment
  resetRecruitmentForNewMonth(state);

  // New race of month
  state.raceOfMonth = getRandomRace();

  // New recruitable gladiators and discounts
  createRecruitableGladiators(state);
  generateDiscountedItems(state);
}

// Award kill leader bonus at end of season
function awardKillLeaderBonus(state: GameState): void {
  let topKiller: { gladiatorId: string; teamId: string; kills: number } | null = null;

  for (const [id, gladiator] of state.gladiators) {
    if (!gladiator.ownerId) continue;
    if (!topKiller || gladiator.takeOuts1 > topKiller.kills) {
      topKiller = {
        gladiatorId: id,
        teamId: gladiator.ownerId,
        kills: gladiator.takeOuts1,
      };
    }
  }

  if (topKiller && topKiller.kills > 0) {
    const team = state.teams.get(topKiller.teamId);
    if (team) {
      modifyMoney(team, GAME_CONSTANTS.KILL_LEADER_BONUS);
      console.log(`Kill leader bonus: ${GAME_CONSTANTS.KILL_LEADER_BONUS} kr awarded to ${team.name}`);
    }
  }
}

// Progress to new year
function progressToNewYear(state: GameState): void {
  // Award kill leader bonus before resetting stats
  awardKillLeaderBonus(state);

  // Reset teams for new year
  for (const [, team] of state.teams) {
    resetTeamForNewYear(team);
  }

  // Age and retire gladiators
  const retiredIds: string[] = [];
  for (const [id, gladiator] of state.gladiators) {
    resetForNewYear(gladiator);
    if (shouldRetire(gladiator)) {
      retiredIds.push(id);
    }
  }

  // Handle retirements
  for (const id of retiredIds) {
    const gladiator = state.gladiators.get(id);
    if (gladiator && gladiator.ownerId) {
      const team = state.teams.get(gladiator.ownerId);
      if (team) {
        // Sell equipment
        for (const item of gladiator.items) {
          if (item) {
            modifyMoney(team, getSellPrice(item));
          }
        }
        removeGladiator(team, id);
      }
    }
    state.gladiators.delete(id);
  }

  // Handle division changes (promotion/relegation)
  handleDivisionChanges(state);
}

// Handle promotion/relegation
function handleDivisionChanges(state: GameState): void {
  // Sort teams in each division by wins
  for (const division of state.divisions) {
    division.teamIds.sort((a, b) => {
      const teamA = state.teams.get(a);
      const teamB = state.teams.get(b);
      return (teamB?.wins ?? 0) - (teamA?.wins ?? 0);
    });
  }

  // Move teams between divisions
  const moves: { teamId: string; fromDiv: number; toDiv: number }[] = [];

  // Last place in div 1 goes to div 2
  if (state.divisions[0].teamIds.length > 0) {
    const lastId = state.divisions[0].teamIds[state.divisions[0].teamIds.length - 1];
    moves.push({ teamId: lastId, fromDiv: 0, toDiv: 1 });
  }

  // First place in div 2 goes to div 1, last place to div 3
  if (state.divisions[1].teamIds.length > 0) {
    moves.push({ teamId: state.divisions[1].teamIds[0], fromDiv: 1, toDiv: 0 });
    moves.push({ teamId: state.divisions[1].teamIds[state.divisions[1].teamIds.length - 1], fromDiv: 1, toDiv: 2 });
  }

  // First place in div 3 goes to div 2, last place to div 4
  if (state.divisions[2].teamIds.length > 0) {
    moves.push({ teamId: state.divisions[2].teamIds[0], fromDiv: 2, toDiv: 1 });
    moves.push({ teamId: state.divisions[2].teamIds[state.divisions[2].teamIds.length - 1], fromDiv: 2, toDiv: 3 });
  }

  // First place in div 4 goes to div 3
  if (state.divisions[3].teamIds.length > 0) {
    moves.push({ teamId: state.divisions[3].teamIds[0], fromDiv: 3, toDiv: 2 });
  }

  // Execute moves
  for (const move of moves) {
    const fromIdx = state.divisions[move.fromDiv].teamIds.indexOf(move.teamId);
    if (fromIdx !== -1) {
      state.divisions[move.fromDiv].teamIds.splice(fromIdx, 1);
      state.divisions[move.toDiv].teamIds.push(move.teamId);
      const team = state.teams.get(move.teamId);
      if (team) {
        team.division = move.toDiv;
      }
    }
  }
}

// Get team by ID
export function getTeam(state: GameState, teamId: string): TeamData | undefined {
  return state.teams.get(teamId);
}

// Get gladiator by ID
export function getGladiator(state: GameState, gladiatorId: string): GladiatorData | undefined {
  return state.gladiators.get(gladiatorId);
}

// Get current team
export function getCurrentTeam(state: GameState): TeamData | undefined {
  return state.currentTeamId ? state.teams.get(state.currentTeamId) : undefined;
}

// Get current gladiator
export function getCurrentGladiator(state: GameState): GladiatorData | undefined {
  return state.currentGladiatorId ? state.gladiators.get(state.currentGladiatorId) : undefined;
}

// Get teams in a division
export function getTeamsInDivision(state: GameState, divisionIndex: number): TeamData[] {
  const teams: TeamData[] = [];
  for (const teamId of state.divisions[divisionIndex].teamIds) {
    const team = state.teams.get(teamId);
    if (team) teams.push(team);
  }
  return teams;
}

// Get division for a team
export function getTeamDivision(state: GameState, teamId: string): Division | undefined {
  const team = state.teams.get(teamId);
  if (!team) return undefined;
  return state.divisions[team.division];
}
